import { ClipboardDocumentIcon, ExclamationTriangleIcon, TrashIcon } from "@heroicons/react/20/solid";
import { useMemo } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Navigate, useParams } from "react-router-dom";
import LoadingContent from "../common/LoadingContent";
import Bank from "../employees/bank/Bank";
import EmployeeDetails from "../employees/personal/EmployeeDetails";
import PreviousEarnings from "../employees/previous-earnings/PreviousEarnings";
import RoleDetails from "../employees/role/RoleDetails";
import TaxDetails from "../employees/tax-details/TaxDetails";

import { Actions as AUDIT_ACTIONS } from "@/redux/actions/auditLogActions";
import { Actions as EMPLOYEE_ACTIONS } from "@/redux/actions/employeeActions";
import { Actions as NOTIFICATION_ACTIONS } from "@/redux/actions/notificationActions";
import { NotificationIcons as icons } from "./NotificationIcons";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import PensionDetails from "../employees/pension/PensionDetails";
import { useEffect } from "react";
import DefaultNotification from "./DefaultNotification";
import EmployeeSalary from "../employees/salary-details/EmployeeSalary";

const reviewChanges = ["agreement", "role", "salary", "previous earnings", "tax", "bank", "pension"];

const elementTypes = (type) => {
	switch (type) {
		case "agreement":
			return EmployeeDetails;
		case "role":
			return RoleDetails;
		case "salary":
			return EmployeeSalary;
		case "previous earnings":
			return PreviousEarnings;
		case "tax":
			return TaxDetails;
		case "bank":
			return Bank;
		case "pension":
			return PensionDetails;
		default:
			return DefaultNotification;
	}
};

const Notification = ({
	type: elementType,
	agreement,
	description,
	title,
	auditLog,
	viewDetails,
	entry,
	notification,
	loading,
	getEmployeeDetails,
	clearEmployee,
	loadingEmployee,
	deletable,
	deleteNotification,
	content,
}) => {
	let { id } = useParams();
	const Icon = icons[elementType];

	const type = useMemo(() => {
		return elementType;
	}, [elementType]);

	const Element = elementTypes(type);

	useEffect(() => {
		if (reviewChanges.includes(type) && agreement) getEmployeeDetails(agreement);
	}, [agreement]);

	useEffect(() => {
		return () => {
			clearEmployee();
		};
	}, []);

	if (loading || loadingEmployee) return <LoadingContent />;
	if (!notification && !loading) return <Navigate to="/notifications" />;

	const warnings = auditLog?.issues?.filter((i) => i.type === "warning") || [];
	const errors = auditLog?.issues?.filter((i) => i.type === "error") || [];

	return (
		<>
			<div className="content bg-gray-lighter">
				<div className="col-xs-7">
					<div
						className="pull-left"
						style={{ marginBottom: "1rem" }}
					>
						<Link
							to={`/notifications/`}
							style={{ display: "flex", flexDirection: "row" }}
						>
							{Icon ? <ArrowLeftIcon style={{ height: "4rem", marginLeft: "-1rem" }} /> : null}
							<h3 style={{ marginLeft: "0.5rem", marginTop: "0.5rem" }}>Go Back</h3>
						</Link>
					</div>
				</div>
				<div className="row">
					<div className="col-xs-7">
						<div className="pull-left">
							{Icon ? <Icon style={{ height: "6rem", marginRight: "1.5rem" }} /> : null}
						</div>
						<div className="pull-left">
							<h1 className="page-heading">{title ? title : null}</h1>
							<p>{description}</p>
						</div>
					</div>

					<div className="col-xs-5">
						{warnings?.length ? (
							<button
								className="btn btn-warning pull-right"
								style={{ marginTop: "1.5rem", marginLeft: "0.5rem" }}
								onClick={() => {
									viewDetails(auditLog);
								}}
							>
								<ExclamationTriangleIcon style={{ height: "1.5em", float: "left" }} />
								<div style={{ float: "left" }}>
									{warnings?.length} Warning{warnings?.length > 1 ? "s" : ""}
								</div>
							</button>
						) : null}
						{errors?.length ? (
							<button
								className="btn btn-danger pull-right"
								style={{ marginTop: "1.5rem", marginLeft: "0.5rem" }}
								onClick={() => {
									viewDetails(auditLog);
								}}
							>
								<ExclamationTriangleIcon style={{ height: "1.5em", float: "left" }} />
								<div style={{ float: "left" }}>
									{errors?.length} Error{errors?.length > 1 ? "s" : ""}
								</div>
							</button>
						) : null}
						{auditLog?.diff ? (
							<button
								className="btn btn-default pull-right"
								style={{ marginTop: "1.5rem", marginLeft: "0.5rem" }}
								onClick={() => {
									viewDetails(auditLog);
								}}
							>
								<ClipboardDocumentIcon style={{ height: "1.5em", float: "left" }} />
								<div style={{ float: "left" }}>Audit Log</div>
							</button>
						) : null}
						{deletable ? (
							<button
								className="btn btn-danger pull-right"
								style={{ marginTop: "1.5rem" }}
								onClick={() => deleteNotification(id)}
							>
								<TrashIcon style={{ height: "1.5em", float: "left" }} />
								<div style={{ float: "left" }}>Delete</div>
							</button>
						) : null}
					</div>
				</div>
			</div>
			<div className="px-8 mb-20">
				<Element
					forReview={entry}
					forReviewAgreement={agreement}
					notification={id}
					content={content}
				/>
				<div className="clearfix" />
			</div>
		</>
	);
};

const mapStateToProps = (state, ownProps) => {
	const { type, description, title, source, read, entry, agreement, auditLog, deletable, content } = {
		...state.notifications?.notification,
	};

	const loadingEmployee = agreement && !state.employee?.agreementInfo?.id && reviewChanges.includes(type);

	return {
		type,
		description,
		title,
		source,
		read,
		entry,
		auditLog,
		content,
		deletable,
		agreement,
		notification: !!state.notifications?.notification?.id,
		loading: state.notifications?.notification?.loading,
		loadingEmployee,
	};
};

const mapDispatchToProps = (dispatch) => ({
	deleteNotification: (id) => dispatch(NOTIFICATION_ACTIONS.deleteNotification(id)),
	viewDetails: (item) => dispatch(AUDIT_ACTIONS.showAuditLogDetail({ show: true, item })),
	clearEmployee: () => dispatch(EMPLOYEE_ACTIONS.clearEmployee()),
	getEmployeeDetails: (employeeId) => dispatch(EMPLOYEE_ACTIONS.getEmployeeDetails(employeeId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Notification);
